/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { DateTime, Duration } from 'luxon';
import { map, Observable } from 'rxjs';
import { LegView } from '@sequence/models/view/leg.view';
import { DataItem } from '@shared/models/data-item';
import { AlertService } from '@shared/services/alert/alert.service';
import { ReleaseRequest } from './models/request/release.request';
import { MelView } from '../../shared/models/mel.view';
import { ReleaseView } from './models/view/release.view';
import { ReleaseService } from './release.service';
import { RequestState } from '@shared/models/request-state';
import { ReleaseResponse } from './models/response/release.response';

@Injectable({
  providedIn: 'root',
})
export class ReleaseStore {
  refreshFn = (request: ReleaseRequest) => this.getRelease(request, true);
  constructor(
    private releaseService: ReleaseService,
    private alertService: AlertService
  ) {}

  /** Get release using cache strategy. */
  getRelease(
    request: ReleaseRequest,
    isRefresh = false
  ): Observable<RequestState<ReleaseView>> {
    return this.releaseService
      .getRelease(request, isRefresh)
      .pipe(
        map((releaseResponse) =>
          this.mapReleaseResponse(releaseResponse, request.leg)
        )
      );
  }

  // eslint-disable-next-line complexity
  private mapReleaseResponse(
    releaseResponse: RequestState<ReleaseResponse>,
    leg: LegView
  ): RequestState<ReleaseView> {
    if (releaseResponse.hasError) {
      this.showErrorAlert();
    }

    if (!releaseResponse.response) {
      return releaseResponse.into<ReleaseView>();
    }

    const release = releaseResponse.response!;

    // For flightkeys flights, a message will be displayed informing user that this release isn't supported.
    if (release.source.includes('FKY OWNED')) {
      const releaseView: ReleaseView = {
        timestamp: DateTime.fromISO(release.timestamp, { zone: 'utc' }),
        summary: [],
        fuel: [],
        aircraft: {
          dataItems: [],
          mels: [],
          nefs: [],
        },
        filedRoute: [],
        text: release.takeoffPerformance + release?.source || '',
      };

      return releaseResponse.into<ReleaseView>(releaseView);
    }

    // Summary
    let alternateData: DataItem = {
      label: 'Alternate',
      value: release.alternate || 'NONE',
    };

    // Set styling if alternate exists.
    if (!release.alternate || release.alternate === 'NONE') {
      alternateData.style = 'success';
    } else {
      alternateData.style = 'caution';
    }

    // If two alternates exist.
    if (release.secondAlternate) {
      alternateData = {
        label: 'Alternates',
        value: `${release.alternate} ${release.secondAlternate}`,
        style: 'caution',
      };
    }

    const summary: DataItem[] = [
      {
        label: 'Flight Time',
        value: Duration.fromISOTime(release.flightTime).toFormat('h:mm'),
        style: 'bold',
      },
      alternateData,
    ];

    // Add T/O alternate if exists.
    if (release.takeoffAlternate) {
      summary.push({
        label: 'T/O Alt',
        value: release.takeoffAlternate,
        style: 'caution',
      });
    }

    // Fuel
    const fuel: DataItem[] = [
      { label: 'Release', value: this.truncateFuel(release.fuelRelease) },
      {
        label: 'Min T/O',
        value: this.truncateFuel(release.fuelMinimumTakeoff),
      },
      { label: 'Arrival', value: this.truncateFuel(release.fuelArrival) },
    ];

    // Dispatcher
    const dispatcher = {
      name: release.dispatcherName,
      desk: release.dispatcherDeskNumber,
      phone: release.dispatcherPhone,
      remarks: release.dispatcherRemarks,
    };

    // Aircraft
    const aircraft = {
      dataItems: [
        { label: 'Type', value: leg.aircraft.short || '' },
        { label: 'Tail', value: release.registration },
        { label: 'Nose', value: release.aircraftNumber },
      ],
      mels: this.parseMELs(release.minimumEquipmentListItems),
      nefs: this.parseMELs(release.nonEssentialFurnishings),
    };

    // Filed Route. Split route into string array and remove 'DCT' and the
    // starting lat/long (N044...).
    const filedRoute = release.routing
      .split(/\s/)
      .filter((fix) => fix !== '' && fix !== 'DCT')
      .slice(1) // Remove the starting lat/long
      .map((fix) => fix.split('/')[0]); // If a fix has an altitude change: ZUN/N048...370, remove everything after/including the '/'

    const releaseView: ReleaseView = {
      timestamp: DateTime.fromISO(release.timestamp, { zone: 'utc' }),
      summary,
      fuel,
      dispatcher,
      aircraft,
      filedRoute,
      isWeightRestricted: this.isWeightRestricted(release.takeoffPerformance),
      text: release.takeoffPerformance + release?.source || '',
    };

    return releaseResponse.into<ReleaseView>(releaseView);
  }

  /** Parse parts of MEL text for display. */
  private parseMELs(melText: string): MelView[] {
    // Examples:
    //           1         2         3         4         5         6
    // 0123456789012345678901234567890123456789012345678901234567890123456
    // MEL 0278D  2757C06    80-01        *F ENG START VALVE    80 01
    // MEL 0277D  2224D21    25-01A          CAPT SEAT ELEC ADJ 25 01A

    const mels = melText.split('\n');

    // The min line length needed to parse using substring indices.
    const minMelLength = 56;

    return mels
      .map((mel) => {
        if (mel.length > minMelLength) {
          return {
            type: mel.substring(0, 3),
            id: mel.substring(19, 30).trim(),
            modifier: mel.substring(34, 37).trim(),
            description: mel.substring(38, 57).trim(),
          };
        } else {
          return { type: '', id: '', description: '', modifier: '' };
        }
      })
      .filter((mel) => mel.type !== '');
  }

  /** Reformat fuel number to a thousands decimal, truncating the hundreds value. */
  private truncateFuel(fuelValue: number): string {
    // We don't want a rounded value, otherwise toFixed would work by itself.
    return (fuelValue / 1000).toFixed(3).slice(0, -2);
  }

  private isWeightRestricted(text: string): boolean {
    return text.includes('* WEIGHT RESTRICTED FLIGHT *');
  }

  /** Show alert then return empty ReleaseResponse */
  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get release data.',
    });
  }
}
